import * as React from "react"
import { useTranslation } from "react-i18next"
import {
  GroupBase,
  ContainerProps,
  ValueContainerProps,
  IndicatorsContainerProps,
} from "react-select"
import { clx, Label } from "~/design-system"
import InputError from "~/components/atoms/input-error"

type AdjacentContainerProps = {
  label?: string
  required?: boolean
  name?: string
  children?: React.ReactNode
} & React.ComponentPropsWithRef<"div">

export const AdjacentContainer = React.forwardRef<
  HTMLDivElement,
  AdjacentContainerProps
>(
  (
    {
      label,
      required,
      name,
      children,
      className,
    }: AdjacentContainerProps,
    ref
  ) => {
    return (
      <div className={clx("nodrag gap-y-2 flex w-full flex-col cursor-default", className)} ref={ref}>
        {label && (
          <Label
            id={`${name}_label`}
            htmlFor={name}
            required={required}>
            {label}
          </Label>
        )}
        {children}
      </div>
    )
  }
)

export const SelectContainer = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>({
  innerProps,
  className,
  children,
}: ContainerProps<Option, IsMulti, Group>) => {
  return (
    <div
      {...innerProps}
      className={clx("pointer-events-auto relative", className)}
    >
      {children}
    </div>
  )
}

export const ValueContainer = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(
  props: ValueContainerProps<Option, IsMulti, Group>
) => {
  const { t } = useTranslation()
  const {
    className,
    children,
    innerProps,
    isMulti,
    selectProps: { value, inputValue, label, selectedPlaceholder, isDisabled },
  } = props
  if (isMulti && Array.isArray(value)) {
    return (
      <div
        {...innerProps}
        className={clx(
          "scrolling-touch group relative flex flex-1 items-center overflow-hidden",
          {
            "gap-1": isMulti,
          },
          className
        )}
      >
        {value?.length > 0 && (
          <div className="bg-grey-20 text-ui-fg-subtle px-small txt-small-regular-plus rounded-rounded gap-x-1 flex h-7 cursor-default items-center">
            <span>{value.length}</span>
          </div>
        )}
        <div className="relative grow">
          {children}
          {value?.length > 0 && inputValue === "" && (
            <span className={clx(
              "txt-base-regular absolute top-1/2 -translate-y-1/2",
              { "text-ui-fg-muted": isDisabled },
            )}>
              {selectedPlaceholder || label || t("Selected")}
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      {...innerProps}
      className={clx(
        "scrolling-touch relative flex items-center overflow-hidden w-full txt-base-regular text-ui-fg-base",
        {
          "gap-1": isMulti,
        },
        { "text-ui-fg-muted": isDisabled },
        className
      )}
    >
      {children}
    </div>
  )
}

export const IndicatorsContainer = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>({
  className,
  innerProps,
  selectProps: { isDisabled },
  children,
}: IndicatorsContainerProps<Option, IsMulti, Group>) => {
  return (
    <div
      {...innerProps}
      className={clx(
        "text-ui-fg-subtle gap-x-small px-small flex items-center",
        { "text-ui-fg-faint": isDisabled },
        className
      )}
    >
      {children}
    </div>
  )
}
